import React from 'react';
import { Clock, Check, X, Euro, User } from 'lucide-react';
import { useServiceViewModel } from '../../view-model/professional/company/service-view-model';
import { observer } from 'mobx-react-lite';

interface ServiceViewProps {
  serviceId: string;
}

const ServiceView: React.FC<ServiceViewProps> = observer(({ serviceId }) => {
  const viewModel = useServiceViewModel();

  const service = viewModel.getServiceFromStore(serviceId);
  const categories = viewModel.getCategoriesByServiceId(serviceId);

  if (!service) return null;

  return (
    <div className="space-y-5">
      {/* En-tête avec image et informations principales */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Image du service */}
        {service.image && (
          <div className="w-full md:w-48 h-48 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex-shrink-0">
            <img
              src={service.image}
              alt={service.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Informations principales */}
        <div className="flex-1 space-y-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{service.name}</h2>
            {service.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {service.description}
              </p>
            )}
          </div>

          {/* Prix et durée */}
          <div className="flex items-center gap-4">
            <div className="flex items-center text-lg font-semibold text-gray-900 dark:text-white">
              <Euro size={18} className="mr-1" />
              <span>{parseFloat(service.price.toString()).toFixed(2)} €</span>
            </div>

            {service.duration && service.duration > 0 && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <Clock size={16} className="mr-1" />
                <span>
                  {service.duration >= 60
                    ? `${Math.floor(service.duration / 60)}h${service.duration % 60 > 0 ? service.duration % 60 : ''}`
                    : `${service.duration} min`}
                </span>
              </div>
            )}
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${service.isActive ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' : 'bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-400'}`}>
              {service.isActive ? <Check size={12} /> : <X size={12} />}
              {service.isActive ? 'Active' : 'Inactive'}
            </span>

            {service.requiresBooking && (
              <span className="inline-flex items-center text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                Réservation requise
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Paramètres de réservation */}
      {service.requiresBooking && (service.minTimeBeforeBooking || service.bufferTimeBetweenSlots) && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase">Paramètres de réservation</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {service.minTimeBeforeBooking && (
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Délai minimum</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.minTimeBeforeBooking >= 60
                      ? `${Math.floor(service.minTimeBeforeBooking / 60)}h${service.minTimeBeforeBooking % 60 > 0 ? service.minTimeBeforeBooking % 60 : ''}`
                      : `${service.minTimeBeforeBooking} min`}
                  </p>
                </div>
              </div>
            )}

            {service.bufferTimeBetweenSlots && (
              <div className="flex items-start gap-2">
                <Clock size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Temps tampon</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {service.bufferTimeBetweenSlots >= 60
                      ? `${Math.floor(service.bufferTimeBetweenSlots / 60)}h${service.bufferTimeBetweenSlots % 60 > 0 ? service.bufferTimeBetweenSlots % 60 : ''}`
                      : `${service.bufferTimeBetweenSlots} min`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section Collaborateurs */}
      {service.collaborators && service.collaborators.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase">Collaborateurs assignés</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {service.collaborators.map(collaborator => (
              <div
                key={collaborator.collaboratorCompagnieId}
                className="flex items-center gap-2 p-2 rounded border border-gray-200 dark:border-gray-700"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                  {collaborator.avatar ? (
                    <img
                      src={collaborator.avatar}
                      alt={`${collaborator.firstName} ${collaborator.lastName}`}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User size={16} className="text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {collaborator.firstName} {collaborator.lastName}
                  </div>
                  {collaborator.profession && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {collaborator.profession}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Catégories et options */}
      {categories.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase">Catégories et options</h4>
          <div className="space-y-3">
            {categories.map(category => (
              <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {/* En-tête de la catégorie */}
                <div className="bg-gray-50 dark:bg-gray-800/50 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h5 className="font-medium text-sm text-gray-900 dark:text-white">{category.name}</h5>
                      {category.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">• {category.description}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {category.isRequired && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          Obligatoire
                        </span>
                      )}
                      {category.isMultipleChoice && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          Multiple
                        </span>
                      )}
                      {!category.isActive && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Options de la catégorie */}
                <div className="p-3">
                  {category.options && category.options.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {category.options.map(option => (
                        <div key={option.id} className="flex items-center gap-2 p-2 border border-gray-200 dark:border-gray-700 rounded">
                          {/* Image de l'option */}
                          {option.image && (
                            <div className="w-12 h-12 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                              <img
                                src={option.image}
                                alt={option.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          {/* Détails de l'option */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h6 className="font-medium text-sm text-gray-900 dark:text-white truncate">{option.name}</h6>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white flex-shrink-0">
                                {option.price && parseFloat(option.price.toString()) > 0 ? `+${option.price} €` : 'Gratuit'}
                              </span>
                            </div>

                            {option.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{option.description}</p>
                            )}

                            {!option.isActive && (
                              <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400 mt-1">
                                Inactive
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                      Aucune option
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

export default ServiceView;
